import { Link } from 'react-router-dom';
import type { Article } from '@/types';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer no-underline"
      >
        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-400">
            <span className="whitespace-nowrap">{article.publishedAt}</span>
            <span className="whitespace-nowrap">{article.views.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer no-underline"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-3 whitespace-nowrap">
            {article.category}
          </span>
          <h3 className="text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-snug" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 mt-2 line-clamp-2 leading-relaxed">{article.excerpt}</p>
          <div className="flex items-center gap-4 mt-4 text-xs text-foreground-400">
            <span className="whitespace-nowrap flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime} 分钟阅读
            </span>
            <span className="whitespace-nowrap flex items-center gap-1">
              <i className="ri-eye-line"></i> {article.views.toLocaleString()}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer no-underline"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4 md:p-5">
        <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-2.5 whitespace-nowrap">
          {article.category}
        </span>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-snug" style={{ fontFamily: 'var(--font-heading)' }}>
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 mt-2 line-clamp-2 leading-relaxed">{article.excerpt}</p>
        <div className="flex items-center gap-4 mt-3 text-xs text-foreground-400">
          <span className="whitespace-nowrap">{article.publishedAt}</span>
          <span className="whitespace-nowrap">{article.views.toLocaleString()} 阅读</span>
        </div>
      </div>
    </Link>
  );
}